'use client';

import React from 'react';

interface PricingBreakdownProps {
  pricePerNight: number;
  currency: string;
  checkIn: string;
  checkOut: string;
  cleaningFee?: number;
  serviceFee?: number;
  taxRate?: number;
  discount?: number;
}

export default function PricingBreakdown({
  pricePerNight,
  currency,
  checkIn,
  checkOut,
  cleaningFee = 0,
  serviceFee = 0,
  taxRate = 0.18,
  discount = 0,
}: PricingBreakdownProps) {
  const nights = checkIn && checkOut
    ? Math.max(1, Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24)))
    : 1;

  const accommodation = pricePerNight * nights;
  const subtotal = accommodation + cleaningFee + serviceFee;
  const discountAmount = discount > 0 ? Math.round(subtotal * discount) : 0;
  const taxable = subtotal - discountAmount;
  const taxes = Math.round(taxable * taxRate);
  const total = taxable + taxes;

  const fmt = (n: number) => `${currency} ${n.toLocaleString()}`;

  return (
    <div className="space-y-3">
      <div className="flex justify-between text-sm text-gray-700">
        <span>{fmt(pricePerNight)} × {nights} night{nights !== 1 ? 's' : ''}</span>
        <span>{fmt(accommodation)}</span>
      </div>
      {cleaningFee > 0 && (
        <div className="flex justify-between text-sm text-gray-700">
          <span>Cleaning fee</span>
          <span>{fmt(cleaningFee)}</span>
        </div>
      )}
      {serviceFee > 0 && (
        <div className="flex justify-between text-sm text-gray-700">
          <span>Service fee</span>
          <span>{fmt(serviceFee)}</span>
        </div>
      )}
      {discountAmount > 0 && (
        <div className="flex justify-between text-sm text-green-600">
          <span>Discount ({Math.round(discount * 100)}%)</span>
          <span>−{fmt(discountAmount)}</span>
        </div>
      )}
      <div className="flex justify-between text-sm text-gray-700">
        <span>Taxes (18% VAT)</span>
        <span>{fmt(taxes)}</span>
      </div>
      <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-gray-900">
        <span>Total</span>
        <span>{fmt(total)}</span>
      </div>
    </div>
  );
}
